import { DragDropContext, type OnDragEndResponder } from "@hello-pangea/dnd";
import isEqual from "lodash/isEqual";
import { useDataProvider, useListContext, type DataProvider } from "ra-core";
import { useEffect, useState } from "react";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company } from "../types";
import { CompanyColumn } from "./CompanyColumn";
import type { CompaniesByStage } from "./stages";
import { getCompaniesByStage } from "./stages";

export const CompanyKanbanContent = () => {
  const { companyLifecycleStages } = useConfigurationContext();
  const {
    data: unorderedCompanies,
    isPending,
    refetch,
  } = useListContext<Company>();
  const dataProvider = useDataProvider();

  const [companiesByStage, setCompaniesByStage] = useState<CompaniesByStage>(
    getCompaniesByStage([], companyLifecycleStages),
  );

  useEffect(() => {
    if (unorderedCompanies) {
      const newCompaniesByStage = getCompaniesByStage(
        unorderedCompanies,
        companyLifecycleStages,
      );
      if (!isEqual(newCompaniesByStage, companiesByStage)) {
        setCompaniesByStage(newCompaniesByStage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unorderedCompanies]);

  if (isPending) return null;

  const onDragEnd: OnDragEndResponder = (result) => {
    const { destination, source } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const sourceStage = source.droppableId;
    const destinationStage = destination.droppableId;
    const sourceCompany = companiesByStage[sourceStage][source.index]!;
    const destinationCompany = companiesByStage[destinationStage][
      destination.index
    ] ?? {
      lifecycle_stage: destinationStage,
      index: undefined, // undefined if dropped after the last item
    };

    // compute local state change synchronously
    setCompaniesByStage(
      updateCompanyStageLocal(
        sourceCompany,
        { stage: sourceStage, index: source.index },
        { stage: destinationStage, index: destination.index },
        companiesByStage,
      ),
    );

    // persist the changes
    updateCompanyStage(
      sourceCompany,
      destinationCompany as any,
      dataProvider,
    ).then(() => {
      refetch();
    });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {companyLifecycleStages.map((stage) => (
          <CompanyColumn
            stage={stage.value}
            companies={companiesByStage[stage.value] || []}
            key={stage.value}
          />
        ))}
      </div>
    </DragDropContext>
  );
};

const updateCompanyStageLocal = (
  sourceCompany: Company,
  source: { stage: string; index: number },
  destination: {
    stage: string;
    index?: number; // undefined if dropped after the last item
  },
  companiesByStage: CompaniesByStage,
) => {
  if (source.stage === destination.stage) {
    // moving company inside the same column
    const column = [...(companiesByStage[source.stage] || [])];
    column.splice(source.index, 1);
    column.splice(destination.index ?? column.length + 1, 0, sourceCompany);
    return {
      ...companiesByStage,
      [destination.stage]: column,
    };
  } else {
    // moving company across columns
    const sourceColumn = [...(companiesByStage[source.stage] || [])];
    const destinationColumn = [...(companiesByStage[destination.stage] || [])];
    sourceColumn.splice(source.index, 1);
    destinationColumn.splice(
      destination.index ?? destinationColumn.length + 1,
      0,
      { ...sourceCompany, lifecycle_stage: destination.stage },
    );
    return {
      ...companiesByStage,
      [source.stage]: sourceColumn,
      [destination.stage]: destinationColumn,
    };
  }
};

const updateCompanyStage = async (
  source: Company,
  destination: {
    lifecycle_stage: string;
    index?: number; // undefined if dropped after the last item
  },
  dataProvider: DataProvider,
) => {
  const sourceStage = source.lifecycle_stage || "Lead";
  if (sourceStage === destination.lifecycle_stage) {
    // moving company inside the same column
    const { data: columnCompanies } = await dataProvider.getList("companies", {
      sort: { field: "index", order: "ASC" },
      pagination: { page: 1, perPage: 100 },
      filter: { lifecycle_stage: sourceStage },
    });
    const destinationIndex = destination.index ?? columnCompanies.length + 1;
    const sourceIndex = source.index || 0;

    if (sourceIndex > destinationIndex) {
      await Promise.all([
        ...columnCompanies
          .filter(
            (company) =>
              (company.index || 0) >= destinationIndex &&
              (company.index || 0) < sourceIndex,
          )
          .map((company) =>
            dataProvider.update("companies", {
              id: company.id,
              data: { index: (company.index || 0) + 1 },
              previousData: company,
            }),
          ),
        dataProvider.update("companies", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    } else {
      await Promise.all([
        ...columnCompanies
          .filter(
            (company) =>
              (company.index || 0) <= destinationIndex &&
              (company.index || 0) > sourceIndex,
          )
          .map((company) =>
            dataProvider.update("companies", {
              id: company.id,
              data: { index: (company.index || 0) - 1 },
              previousData: company,
            }),
          ),
        dataProvider.update("companies", {
          id: source.id,
          data: { index: destinationIndex },
          previousData: source,
        }),
      ]);
    }
  } else {
    // moving company across columns
    const [{ data: sourceCompanies }, { data: destinationCompanies }] =
      await Promise.all([
        dataProvider.getList("companies", {
          sort: { field: "index", order: "ASC" },
          pagination: { page: 1, perPage: 100 },
          filter: { lifecycle_stage: sourceStage },
        }),
        dataProvider.getList("companies", {
          sort: { field: "index", order: "ASC" },
          pagination: { page: 1, perPage: 100 },
          filter: { lifecycle_stage: destination.lifecycle_stage },
        }),
      ]);
    const destinationIndex =
      destination.index ?? destinationCompanies.length + 1;
    const sourceIndex = source.index || 0;

    await Promise.all([
      ...sourceCompanies
        .filter((company) => (company.index || 0) > sourceIndex)
        .map((company) =>
          dataProvider.update("companies", {
            id: company.id,
            data: { index: (company.index || 0) - 1 },
            previousData: company,
          }),
        ),
      ...destinationCompanies
        .filter((company) => (company.index || 0) >= destinationIndex)
        .map((company) =>
          dataProvider.update("companies", {
            id: company.id,
            data: { index: (company.index || 0) + 1 },
            previousData: company,
          }),
        ),
      dataProvider.update("companies", {
        id: source.id,
        data: {
          index: destinationIndex,
          lifecycle_stage: destination.lifecycle_stage,
        },
        previousData: source,
      }),
    ]);
  }
};
